import React, { useState, useEffect } from 'react';
import { agencyService } from '../../services/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CreditCard, DollarSign, History, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const AdminBillingPage = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState({});
  const [expandedAgency, setExpandedAgency] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    currency: 'ARS',
    method: 'MANUAL_CASH',
    period_start: new Date().toISOString().split('T')[0],
    period_end: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
  });

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      const data = await agencyService.getAll();
      setAgencies(data);
    } catch (error) {
      toast.error('Error al cargar agencias');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentHistory = async (agencyId) => {
    if (paymentHistory[agencyId]) {
      // Ya cargado, solo toggle
      setExpandedAgency(expandedAgency === agencyId ? null : agencyId);
      return;
    }

    setLoadingHistory(true);
    try {
      const payments = await agencyService.getPayments(agencyId);
      setPaymentHistory(prev => ({ ...prev, [agencyId]: payments }));
      setExpandedAgency(agencyId);
    } catch (error) {
      toast.error('Error al cargar historial de pagos');
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleRegisterPayment = async (e) => {
    e.preventDefault();
    if (!selectedAgency) return;

    try {
      const payload = {
        amount: parseFloat(paymentData.amount),
        currency: paymentData.currency,
        method: paymentData.method,
        period_start: new Date(paymentData.period_start).toISOString(),
        period_end: new Date(paymentData.period_end).toISOString()
      };

      await agencyService.registerPayment(selectedAgency.id, payload);
      toast.success('Pago registrado correctamente');
      setShowPaymentDialog(false);
      
      // Reset form
      setPaymentData({
        amount: '',
        currency: 'ARS',
        method: 'MANUAL_CASH',
        period_start: new Date().toISOString().split('T')[0],
        period_end: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
      });
      
      // Reload data
      loadAgencies();
      // Refresh history if expanded
      if (expandedAgency === selectedAgency.id) {
        const payments = await agencyService.getPayments(selectedAgency.id);
        setPaymentHistory(prev => ({ ...prev, [selectedAgency.id]: payments }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    }
  };

  const getBillingStatusColor = (status) => {
    const colors = {
      ACTIVE: 'text-green-600 bg-green-50',
      PAST_DUE: 'text-yellow-600 bg-yellow-50',
      SUSPENDED: 'text-red-600 bg-red-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const formatPaymentMethod = (method) => {
    const methods = {
      'MANUAL_CASH': 'Efectivo',
      'MANUAL_TRANSFER': 'Transferencia',
      'MERCADOPAGO': 'MercadoPago'
    };
    return methods[method] || method || 'No configurado';
  };

  const isPastDue = (paidUntil) => {
    if (!paidUntil) return false;
    return new Date(paidUntil) < new Date();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'No configurado';
    try {
      return new Date(dateStr).toLocaleDateString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return 'Fecha inválida';
    }
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">Pagos y Facturación</h1>
        <p className="text-slate-600 mt-2">Gestiona pagos de suscripciones de agencias</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Agencias Activas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {agencies.filter(a => a.billing?.status === 'ACTIVE').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Pagos Vencidos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-yellow-600">
              {agencies.filter(a => isPastDue(a.billing?.paidUntil)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Suspendidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">
              {agencies.filter(a => a.billing?.status === 'SUSPENDED').length}
            </div>
          </CardContent>
        </Card>
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : (
        <div className="space-y-4">
          {agencies.map((agency) => (
            <Card key={agency.id} className={isPastDue(agency.billing?.paidUntil) ? 'border-yellow-400 border-2' : ''}>
              <CardContent className="py-4">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-3">
                      <h3 className="font-bold text-lg">{agency.name}</h3>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getBillingStatusColor(agency.billing?.status)}`}>
                        {agency.billing?.status || 'SIN ESTADO'}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <p className="text-slate-500">Método de Pago</p>
                        <p className="font-medium">
                          {formatPaymentMethod(agency.billing?.paymentMethod)}
                        </p>
                      </div>
                      <div>
                        <p className="text-slate-500">Pagado Hasta</p>
                        <p className={`font-medium ${isPastDue(agency.billing?.paidUntil) ? 'text-red-600' : ''}`}>
                          {formatDate(agency.billing?.paidUntil)}
                        </p>
                        {isPastDue(agency.billing?.paidUntil) && (
                          <p className="text-xs text-red-600 font-medium">⚠️ VENCIDO</p>
                        )}
                      </div>
                      <div>
                        <p className="text-slate-500">Plan</p>
                        <p className="font-medium capitalize">{agency.plan}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => loadPaymentHistory(agency.id)}
                      disabled={loadingHistory}
                    >
                      <History className="w-4 h-4 mr-1" />
                      Historial
                      {expandedAgency === agency.id ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedAgency(agency);
                        setShowPaymentDialog(true);
                      }}
                      size="sm"
                    >
                      <DollarSign className="w-4 h-4 mr-1" />
                      Registrar Pago
                    </Button>
                  </div>
                </div>

                {/* Payment History */}
                {expandedAgency === agency.id && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <History className="w-4 h-4" />
                      Historial de Pagos
                    </h4>
                    {paymentHistory[agency.id]?.length > 0 ? (
                      <div className="space-y-2 overflow-x-auto">
                        <div className="grid grid-cols-5 gap-2 text-xs font-medium text-slate-500 pb-2 border-b min-w-[500px]">
                          <div>Fecha</div>
                          <div>Monto</div>
                          <div>Método</div>
                          <div>Período Desde</div>
                          <div>Período Hasta</div>
                        </div>
                        {paymentHistory[agency.id].map((payment, idx) => (
                          <div key={payment.id || idx} className="grid grid-cols-5 gap-2 text-sm py-2 hover:bg-slate-50 rounded min-w-[500px]">
                            <div>{formatDate(payment.createdAt)}</div>
                            <div className="font-medium">
                              {payment.currency} {payment.amount?.toLocaleString('es-AR')}
                            </div>
                            <div>{formatPaymentMethod(payment.method)}</div>
                            <div>{formatDate(payment.periodStart)}</div>
                            <div>{formatDate(payment.periodEnd)}</div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No hay pagos registrados</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Registrar Pago - {selectedAgency?.name}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleRegisterPayment} className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Monto</label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                placeholder="5000"
                required
                step="0.01"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Moneda</label>
              <Select value={paymentData.currency} onValueChange={(value) => setPaymentData({ ...paymentData, currency: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ARS">ARS (Pesos Argentinos)</SelectItem>
                  <SelectItem value="USD">USD (Dólares)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Método de Pago</label>
              <Select value={paymentData.method} onValueChange={(value) => setPaymentData({ ...paymentData, method: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="MANUAL_CASH">Efectivo</SelectItem>
                  <SelectItem value="MANUAL_TRANSFER">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Período Desde</label>
                <Input
                  type="date"
                  value={paymentData.period_start}
                  onChange={(e) => setPaymentData({ ...paymentData, period_start: e.target.value })}
                  required
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Período Hasta</label>
                <Input
                  type="date"
                  value={paymentData.period_end}
                  onChange={(e) => setPaymentData({ ...paymentData, period_end: e.target.value })}
                  required
                />
              </div>
            </div>
            <Button type="submit" className="w-full">
              <CreditCard className="w-4 h-4 mr-2" />
              Registrar Pago
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBillingPage;
